// Passkey / WebAuthn フロントエンドラッパー。
// @simplewebauthn/browser を使って navigator.credentials.* を呼び出す。
// ZER-334 Phase 1（2026-05-23）

import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser'
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser'
import { request, setCsrfToken } from './_request'

export interface PasskeyCredential {
  id: string
  deviceName: string | null
  deviceType: string | null
  backedUp: boolean
  transports: string[]
  createdAt: string
  lastUsedAt: string | null
}

/** ブラウザが WebAuthn / Passkey に対応しているか */
export function isWebAuthnSupported(): boolean {
  return browserSupportsWebAuthn()
}

/**
 * 認証済みユーザーが Passkey を新規登録する。
 * navigator.credentials.create() を呼び出して生体認証 UI を表示。
 */
export async function registerPasskey(deviceName?: string): Promise<{ ok: true; id: string }> {
  // 1. サーバから challenge + options を取得
  const options = await request<PublicKeyCredentialCreationOptionsJSON>(
    '/auth/webauthn/register/options',
    { method: 'POST', body: JSON.stringify({}) }
  )

  // 2. ブラウザで Passkey 作成（Touch ID / Face ID / Windows Hello 等が起動）
  const registration = await startRegistration({ optionsJSON: options })

  // 3. サーバに送って検証 + 保存
  const result = await request<{ ok: true; id: string }>(
    '/auth/webauthn/register/verify',
    { method: 'POST', body: JSON.stringify({ response: registration, deviceName }) }
  )

  return result
}

/**
 * Passkey でログインする。email 指定時はそのユーザーの credential を絞る。
 * 成功時はサーバが session + CSRF cookie を発行する。
 */
export async function authenticateWithPasskey(email?: string): Promise<{
  user: { id: string; name: string; email: string; role: string }
  csrfToken: string
}> {
  // 1. challenge 取得（公開エンドポイント）
  const optsRes = await fetch('/api/auth/webauthn/authenticate/options', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })
  if (!optsRes.ok) {
    const err = await optsRes.json() as { error?: string }
    throw new Error(err.error || 'チャレンジ取得に失敗しました')
  }
  const options = await optsRes.json() as PublicKeyCredentialRequestOptionsJSON

  // 2. ブラウザで生体認証
  const authentication = await startAuthentication({ optionsJSON: options })

  // 3. サーバに送って検証 + session 発行
  const verifyRes = await fetch('/api/auth/webauthn/authenticate/verify', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ response: authentication }),
  })
  if (!verifyRes.ok) {
    const err = await verifyRes.json() as { error?: string }
    throw new Error(err.error || 'Passkey 認証に失敗しました')
  }
  const data = await verifyRes.json() as {
    user: { id: string; name: string; email: string; role: string }
    csrfToken: string
  }

  // フロント側 csrf 状態を更新
  setCsrfToken(data.csrfToken)
  return data
}

/** 自分の Passkey 一覧を取得 */
export async function listPasskeys(): Promise<PasskeyCredential[]> {
  const res = await request<{ credentials: PasskeyCredential[] }>(
    '/auth/webauthn/credentials',
    { method: 'GET' }
  )
  return res.credentials
}

/** Passkey を削除 */
export async function deletePasskey(id: string): Promise<void> {
  await request<{ ok: true }>('/auth/webauthn/credentials', {
    method: 'DELETE',
    body: JSON.stringify({ id }),
  })
}
